"use client"

import { useDebouncedCallback } from "@/app/hooks/use-debounced-callback"
import { useLingui } from "@lingui/react/macro"
import { InputNumber } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { useCallback, useEffect, useState } from "react"
import { NftFiltersMoleculeReatom } from "./store.reatom"

export const PriceRangeFilter = reatomComponent(() => {
  const { t } = useLingui()

  const { minPriceFilter, maxPriceFilter } = useMolecule(
    NftFiltersMoleculeReatom,
  )

  const minPrice = minPriceFilter()
  const maxPrice = maxPriceFilter()

  const [minInputValue, setMinInputValue] = useState("")
  const [maxInputValue, setMaxInputValue] = useState("")

  const updatePriceRange = useCallback(
    (min?: number, max?: number) => {
      minPriceFilter.set(min)
      maxPriceFilter.set(max)
    },
    [minPriceFilter, maxPriceFilter],
  )

  const debouncedUpdateRange = useDebouncedCallback(updatePriceRange)

  const handleMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMinInputValue(e.target.value)

      const max = getNumberValue(maxInputValue)
      const updatedMin = getNumberValue(e.target.value)
      const updatedMax =
        max !== undefined && updatedMin !== undefined
          ? Math.max(max, updatedMin)
          : max

      debouncedUpdateRange(updatedMin, updatedMax)
    },
    [debouncedUpdateRange, maxInputValue],
  )

  const handleMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMaxInputValue(e.target.value)
      const min = getNumberValue(minInputValue)
      const updatedMax = getNumberValue(e.target.value)
      const updatedMin =
        min !== undefined && updatedMax !== undefined
          ? Math.min(min, updatedMax)
          : min

      debouncedUpdateRange(updatedMin, updatedMax)
    },
    [debouncedUpdateRange, minInputValue],
  )

  useEffect(() => {
    setMinInputValue(minPrice !== undefined ? String(minPrice) : "")
    setMaxInputValue(maxPrice !== undefined ? String(maxPrice) : "")
  }, [minPrice, maxPrice])

  return (
    <div className="flex items-center gap-3">
      <InputNumber
        value={minInputValue}
        onChange={handleMinChange}
        min={0}
        placeholder={t`Min`}
        id="min-price"
      />
      -
      <InputNumber
        value={maxInputValue}
        onChange={handleMaxChange}
        min={0}
        placeholder={t`Max`}
        id="max-price"
      />
    </div>
  )
}, "PriceRangeFilter")

const getNumberValue = (value: string) =>
  value === "" ? undefined : Number(value)
